import React, { useState } from "react";
import { motion } from "framer-motion";
import { pollsApi } from "../../../services/api/pollsApi";
import { FiCheck } from "react-icons/fi";
import "./PollPost.css";

const PollPost = ({ post, onUpdate }) => {
  const [voting, setVoting] = useState(false);

  const poll = post?.poll;
  const totalVotes = poll?.totalVotes || 0;
  const allowMultiple = poll?.allowMultiple;
  const isExpired = poll?.expiresAt && new Date(poll.expiresAt) < new Date();

  // Get current user ID
  const user = JSON.parse(localStorage.getItem("user"));
  const currentUserId = user?.id || user?.userId || user?._id;

  // Helper: Check if user voted (compatible with ObjectId strings)
  const hasUserVoted = (votesArray) => {
    if (!votesArray || !currentUserId) return false;
    return votesArray.some(
      (voteId) => voteId && voteId.toString() === currentUserId.toString()
    );
  };

  // Find which option(s) user voted for
  const userVotedOptions = poll?.options
    ? poll.options
        .map((opt, i) => (hasUserVoted(opt.votes) ? i : -1))
        .filter((i) => i >= 0)
    : [];

  const hasVoted = userVotedOptions.length > 0;

  // Validate poll structure
  if (!post || !poll || !poll.options || !Array.isArray(poll.options)) {
    return (
      <div className="cbx-poll-post cbx-poll-post--error">
        <p>Error: Invalid poll data</p>
      </div>
    );
  }

  // Handle instant vote (WhatsApp style)
  const handleVote = async (optionIndex) => {
    if (voting || hasVoted || isExpired) return;

    setVoting(true);
    try {
      const response = await pollsApi.votePoll(post._id, [optionIndex]);
      const { data } = response.data;

      const updatedPoll = {
        ...poll,
        options: poll.options.map((opt, idx) => ({
          text: opt.text,
          votes: (opt.votes || [])
            .filter((id) => id && id.toString() !== currentUserId.toString())
            .concat(idx === optionIndex ? [currentUserId] : []),
        })),
        totalVotes: data.totalVotes,
      };

      onUpdate(post._id, { poll: updatedPoll });
    } catch (error) {
      alert("Error: " + (error.response?.data?.message || error.message));
    } finally {
      setVoting(false);
    }
  };

  return (
    <div className="cbx-poll-post">
      {post.content && (
        <p className="cbx-poll-post__description">{post.content}</p>
      )}

      <h3 className="cbx-poll-post__question">{poll.question}</h3>

      <div className="cbx-poll-post__options">
        {poll.options.map((option, index) => {
          const voteCount = option.votes?.length ?? 0;
          const percentage =
            totalVotes > 0 ? Math.round((voteCount / totalVotes) * 100) : 0;
          const isSelected = userVotedOptions.includes(index);

          return (
            <motion.div
              key={index}
              className={`cbx-poll-post__option ${
                hasVoted
                  ? "cbx-poll-post__option--voted"
                  : "cbx-poll-post__option--clickable"
              } ${isSelected ? "cbx-poll-post__option--selected" : ""}`}
              onClick={() => !hasVoted && !isExpired && handleVote(index)}
              whileHover={!hasVoted && !isExpired ? { x: 4 } : {}}
              whileTap={!hasVoted && !isExpired ? { scale: 0.99 } : {}}
            >
              <motion.div
                className="cbx-poll-post__progress"
                initial={{ width: 0 }}
                animate={{ width: `${percentage}%` }}
                transition={{ duration: 0.5, ease: "easeOut" }}
              />

              <div className="cbx-poll-post__option-content">
                <span className="cbx-poll-post__option-text">
                  {option.text}
                </span>
                <span className="cbx-poll-post__option-stats">
                  {isSelected && (
                    <span className="cbx-poll-post__checkmark">
                      <FiCheck size={12} />
                    </span>
                  )}
                  {percentage}% ({voteCount})
                </span>
              </div>

              {voting && <div className="cbx-poll-post__voting-indicator" />}
            </motion.div>
          );
        })}
      </div>

      <div className="cbx-poll-post__footer">
        <span>
          {totalVotes} vote{totalVotes !== 1 ? "s" : ""}
        </span>
        {allowMultiple && (
          <span className="cbx-poll-post__footer-separator">•</span>
        )}
        {allowMultiple && <span>Multiple choice</span>}
        {poll.expiresAt && (
          <>
            <span className="cbx-poll-post__footer-separator">•</span>
            <span className={isExpired ? "cbx-poll-post__footer-expired" : ""}>
              {isExpired
                ? "Expired"
                : `Expires ${new Date(poll.expiresAt).toLocaleDateString()}`}
            </span>
          </>
        )}
      </div>
    </div>
  );
};

export default PollPost;
